/**
 * Direct-to-S3 upload for Meta ad videos, with progress.
 *
 * WHY THIS EXISTS — do not replace it with a multipart POST.
 * API Gateway REST APIs cap a request payload at 10 MB and Lambda caps a
 * synchronous invoke payload at 6 MB. Neither is configurable. The old
 * `POST /ads/upload-video-file` multipart route therefore returned
 * `413 Content Too Large` from API Gateway *before Fastify ever ran*, which is
 * why raising `bodyLimit` on that route changed nothing in serverless. Meta's
 * own ceiling is 4 GB, and the only way to honour it is to keep the bytes out
 * of our API entirely.
 *
 * Flow:
 *   1. POST /ads/upload-video/presign        (tiny JSON — well under the cap)
 *   2. PUT the file straight to S3           (API Gateway + Lambda not in this hop)
 *   3. POST /ads/upload-video-url            (Meta pulls the URL via /advideos file_url)
 *
 * XHR, not fetch: a multi-GB upload with no progress bar is indistinguishable
 * from a hang, and `fetch` cannot report upload progress. The upload is also
 * abortable so a user can cancel a 4 GB mistake.
 */
import { API_CONFIG } from "@/lib/api/config";

/** Meta's documented hard ceiling for /advideos. */
export const META_MAX_VIDEO_BYTES = 4 * 1024 * 1024 * 1024;

export interface AdVideoUploadResult {
  /** Publicly fetchable URL handed to Meta as `file_url`. */
  url: string;
  /**
   * Direct S3 URL. CLOUDFRONT_URL does not front the media bucket in every
   * environment, so the backend retries with this if Meta cannot fetch `url`.
   */
  fallbackUrl: string;
  /** S3 object key — diagnostics / future cleanup. */
  key: string;
}

export interface AdVideoUploadOptions {
  /** Receives 0–100. Fires only while bytes are moving to S3. */
  onProgress?: (percent: number) => void;
  /** Abort the in-flight S3 PUT. */
  signal?: AbortSignal;
}

interface PresignResponseBody {
  success: boolean;
  presignedUrl?: string;
  fileUrl?: string;
  fallbackUrl?: string;
  key?: string;
  expiresIn?: number;
  error?: string;
}

/**
 * Human-readable size, for error copy. Kept exported + pure so the size-guard
 * message is unit-testable without touching the network.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // Whole numbers for bytes; one decimal above that.
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}

/**
 * Validate a candidate ad video before any network call. Returns an error
 * string, or null when the file is acceptable.
 */
export function validateAdVideo(file: File | Blob | null | undefined): string | null {
  if (!file || file.size === 0) return "Empty file — nothing to upload";
  if (!file.type || !file.type.startsWith("video/")) {
    return "That file is not a video. Upload an MP4 or MOV.";
  }
  if (file.size > META_MAX_VIDEO_BYTES) {
    return `Video is ${formatBytes(file.size)} — Meta's limit is ${formatBytes(
      META_MAX_VIDEO_BYTES,
    )}. Please compress it and try again.`;
  }
  return null;
}

/** Step 2 as its own function so the XHR/progress/abort logic stays readable. */
function putToS3(
  presignedUrl: string,
  file: File | Blob,
  fileType: string,
  opts: AdVideoUploadOptions,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presignedUrl, true);
    // MUST match the Content-Type sent at presign time or S3 answers 403 —
    // the backend signs it as a conditional parameter.
    xhr.setRequestHeader("Content-Type", fileType);

    const onAbort = () => xhr.abort();
    if (opts.signal) {
      if (opts.signal.aborted) {
        reject(new Error("Upload cancelled"));
        return;
      }
      opts.signal.addEventListener("abort", onAbort, { once: true });
    }
    const cleanup = () => opts.signal?.removeEventListener("abort", onAbort);

    if (opts.onProgress) {
      xhr.upload.onprogress = (e) => {
        // lengthComputable is false for streams of unknown size; skip rather
        // than report a misleading 0%.
        if (e.lengthComputable && e.total > 0) {
          opts.onProgress?.(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      if (xhr.status === 403) {
        reject(new Error("Upload rejected by S3 — the upload link expired. Please try again."));
        return;
      }
      if (xhr.status >= 500) {
        reject(new Error(`S3 is currently unavailable (${xhr.status}). Please retry.`));
        return;
      }
      reject(new Error(`Upload failed (${xhr.status} ${xhr.statusText})`));
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error("Upload failed — network error. Check your connection and retry."));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new Error("Upload cancelled"));
    };

    xhr.send(file);
  });
}

/**
 * Upload an ad video to S3 and return the URL to hand to Meta.
 * Does NOT call Meta — the caller passes `url` to adsApi.uploadVideoByUrl.
 */
export async function uploadAdVideoToS3(
  file: File | Blob,
  opts: AdVideoUploadOptions = {},
): Promise<AdVideoUploadResult> {
  const validationError = validateAdVideo(file);
  if (validationError) throw new Error(validationError);

  const filename = (file as File).name || "ad-video.mp4";
  const fileType = file.type || "video/mp4";

  // Step 1 — presign.
  let presignRes: Response;
  try {
    presignRes = await fetch(`${API_CONFIG.BASE_URL}/ads/upload-video/presign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      },
      body: JSON.stringify({ fileName: filename, fileType }),
      signal: opts.signal,
    });
  } catch (networkErr) {
    if (opts.signal?.aborted) throw new Error("Upload cancelled");
    const msg = networkErr instanceof Error ? networkErr.message : "unreachable";
    throw new Error(`Failed to request upload URL — network error: ${msg}`);
  }

  if (!presignRes.ok) {
    let errorMsg = `Failed to request upload URL (${presignRes.status})`;
    try {
      const body = (await presignRes.json()) as { error?: string };
      if (body?.error) errorMsg = body.error;
    } catch {
      // Not JSON — keep the generic message.
    }
    throw new Error(errorMsg);
  }

  const presignBody = (await presignRes.json()) as PresignResponseBody;
  if (!presignBody.success || !presignBody.presignedUrl || !presignBody.fileUrl) {
    throw new Error(presignBody.error || "Invalid presigned-URL response from server");
  }

  // Step 2 — bytes straight to S3.
  await putToS3(presignBody.presignedUrl, file, fileType, opts);

  return {
    url: presignBody.fileUrl,
    // Older backends don't send it; fall back to the primary so the caller
    // never posts `undefined`.
    fallbackUrl: presignBody.fallbackUrl || presignBody.fileUrl,
    key: presignBody.key || "",
  };
}
